// Page Prestations Desktop - Recherche de services/prestations
// Design cohérent avec ArtisansPage

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Star, MapPin, ChevronRight, Loader2, Wrench, Zap, Droplets,
  Flame, Home, Brush, Hammer, Building2, Waves, TreeDeciduous, Wind, Ruler,
  Euro, Clock, CheckCircle, Shield, Filter
} from 'lucide-react';
import UnifiedHeader from '../../../components/layout/UnifiedHeader';
import MainNavigation from '../../../components/layout/MainNavigation';
import UniversalBanner from '../../../components/layout/UniversalBanner';
import UnifiedFilterSystem, { FilterButton } from '../../../components/filters/UnifiedFilterSystem';
import { useDemoMode } from '../../../components/demo/DemoModeProvider';
import { AdSidebarLeft, AdSidebarRight } from '../../../components/ads/AdBannerSystem';

import { API_URL } from '../../../config/api';

// Types de métiers
const METIERS = [
  { id: 'plomberie', label: 'Plomberie', icon: Droplets, color: '#0EA5E9' },
  { id: 'electricite', label: 'Électricité', icon: Zap, color: '#F59E0B' },
  { id: 'maconnerie', label: 'Maçonnerie', icon: Building2, color: '#6B7280' },
  { id: 'carrelage', label: 'Carrelage', icon: Ruler, color: '#10B981' },
  { id: 'peinture', label: 'Peinture', icon: Brush, color: '#EC4899' },
  { id: 'menuiserie', label: 'Menuiserie', icon: Hammer, color: '#92400E' },
  { id: 'couverture', label: 'Couverture', icon: Home, color: '#7C3AED' },
  { id: 'chauffage', label: 'Chauffage', icon: Flame, color: '#EF4444' },
  { id: 'piscine', label: 'Piscine', icon: Waves, color: '#3B82F6' },
  { id: 'jardinage', label: 'Jardinage', icon: TreeDeciduous, color: '#22C55E' },
  { id: 'climatisation', label: 'Climatisation', icon: Wind, color: '#06B6D4' },
  { id: 'serrurerie', label: 'Serrurerie', icon: Wrench, color: '#64748B' },
];

// Types de tarifs
const TARIF_TYPES = {
  forfait: { label: 'Forfait', color: '#22C55E' },
  horaire: { label: 'Horaire', color: '#3B82F6' },
  metre_carre: { label: 'Au m²', color: '#06B6D4' },
  sur_devis: { label: 'Sur devis', color: '#8B5CF6' },
  gratuit: { label: 'Gratuit', color: '#F97316' },
};

// Mock data prestations
const MOCK_PRESTATIONS = [
  {
    id: 'presta-1',
    name: 'Réparation fuite d\'eau',
    description: 'Intervention rapide pour tous types de fuites. Détection, réparation, remplacement de joints et raccords.',
    metier: 'plomberie',
    tarif_type: 'forfait',
    tarif_montant: 80,
    tarif_unite: '€',
    duration: '1-2h',
    artisan: {
      id: 'artisan-1',
      shop_name: 'Plomberie Martin & Fils',
      city: 'Lyon',
      rating: 4.9,
      reviews_count: 127,
      is_verified: true,
    },
    image: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=800',
    urgent_available: true,
  },
  {
    id: 'presta-2',
    name: 'Installation tableau électrique',
    description: 'Mise aux normes NF C 15-100. Installation, remplacement ou extension de tableau électrique.',
    metier: 'electricite',
    tarif_type: 'sur_devis',
    tarif_montant: null,
    tarif_unite: '€',
    duration: '4-8h',
    artisan: {
      id: 'artisan-2',
      shop_name: 'Élec Pro Méditerranée',
      city: 'Marseille',
      rating: 4.8,
      reviews_count: 89,
      is_verified: true,
    },
    image: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=800',
    urgent_available: false,
  },
  {
    id: 'presta-3',
    name: 'Pose de carrelage',
    description: 'Pose de carrelage sol et mur. Tous formats, tous styles. Préparation du support incluse.',
    metier: 'carrelage',
    tarif_type: 'metre_carre',
    tarif_montant: 45,
    tarif_unite: '€/m²',
    duration: '2-5 jours',
    artisan: {
      id: 'artisan-3',
      shop_name: 'Carrelage Expert',
      city: 'Toulouse',
      rating: 4.7,
      reviews_count: 64,
      is_verified: true,
    },
    image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800',
    urgent_available: false,
  },
  {
    id: 'presta-4',
    name: 'Peinture intérieure',
    description: 'Préparation des surfaces, application de 2 couches, finition soignée. Peinture écologique disponible.',
    metier: 'peinture',
    tarif_type: 'metre_carre',
    tarif_montant: 25,
    tarif_unite: '€/m²',
    duration: '2-4 jours',
    artisan: {
      id: 'artisan-4',
      shop_name: 'Peinture Leroy',
      city: 'Paris',
      rating: 4.9,
      reviews_count: 203,
      is_verified: true,
    },
    image: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=800',
    urgent_available: true,
  },
  {
    id: 'presta-5',
    name: 'Dépannage électrique',
    description: 'Intervention rapide 7j/7 pour panne, court-circuit, disjoncteur. Diagnostic offert.',
    metier: 'electricite',
    tarif_type: 'horaire',
    tarif_montant: 55,
    tarif_unite: '€/h',
    duration: '1-3h',
    artisan: {
      id: 'artisan-2',
      shop_name: 'Élec Pro Méditerranée',
      city: 'Marseille',
      rating: 4.8,
      reviews_count: 89,
      is_verified: true,
    },
    image: 'https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=800',
    urgent_available: true,
  },
  {
    id: 'presta-6',
    name: 'Installation pompe à chaleur',
    description: 'Installation PAC air/air ou air/eau. Étude thermique, pose et mise en service incluses.',
    metier: 'chauffage',
    tarif_type: 'sur_devis',
    tarif_montant: null,
    tarif_unite: '€',
    duration: '2-3 jours',
    artisan: {
      id: 'artisan-5',
      shop_name: 'Chauffage Confort',
      city: 'Bordeaux',
      rating: 4.6,
      reviews_count: 45,
      is_verified: true,
    },
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800',
    urgent_available: false,
  },
];

// Carte Prestation Desktop
function PrestationCard({ prestation, onClick }) {
  const metier = METIERS.find(m => m.id === prestation.metier);
  const MetierIcon = metier?.icon || Wrench;
  const tarifType = TARIF_TYPES[prestation.tarif_type];

  const formatPrice = () => {
    if (prestation.tarif_type === 'sur_devis') return 'Sur devis';
    if (prestation.tarif_type === 'gratuit') return 'Gratuit';
    if (prestation.tarif_montant) {
      const unite = prestation.tarif_unite || '€';
      if (prestation.tarif_type === 'horaire' || unite === '€/h') return `${prestation.tarif_montant}€/h`;
      if (prestation.tarif_type === 'metre_carre' || unite === '€/m²') return `${prestation.tarif_montant}€/m²`;
      return `À partir de ${prestation.tarif_montant}€`;
    }
    return 'Sur devis';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg hover:border-orange-200 transition-all cursor-pointer group"
      onClick={() => onClick(prestation)}
    >
      {/* Image */}
      <div className="relative aspect-[16/10]">
        {prestation.image ? (
          <img 
            src={prestation.image} 
            alt={prestation.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <MetierIcon size={48} className="text-gray-300" />
          </div>
        )}
        
        {/* Badge Métier */}
        <div 
          className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 text-white"
          style={{ backgroundColor: metier?.color || '#6B7280' }}
        >
          <MetierIcon size={14} />
          {metier?.label || 'Service'}
        </div>
        
        {/* Badge Urgent */}
        {prestation.urgent_available && (
          <div className="absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-bold bg-red-500 text-white flex items-center gap-1">
            <Zap size={12} />
            Urgent dispo
          </div>
        )}
        
        {/* Prix */}
        <div className="absolute bottom-3 right-3 px-3 py-1.5 rounded-xl bg-white/95 shadow-md">
          <span className="text-base font-bold text-gray-900">{formatPrice()}</span>
        </div>
      </div>
      
      {/* Contenu */}
      <div className="p-4">
        <h3 className="font-bold text-gray-900 text-lg line-clamp-1 group-hover:text-orange-600 transition-colors">
          {prestation.name}
        </h3>
        <p className="text-sm text-gray-500 line-clamp-2 mt-1">{prestation.description}</p>
        
        {/* Durée estimée */}
        {prestation.duration && (
          <div className="flex items-center gap-1.5 mt-2 text-sm text-gray-500">
            <Clock size={14} />
            <span>Durée estimée : {prestation.duration}</span>
          </div>
        )}
        
        {/* Artisan */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#1E3A5F] flex items-center justify-center">
              <Building2 size={16} className="text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 line-clamp-1">{prestation.artisan.shop_name}</p>
              <div className="flex items-center gap-1">
                <MapPin size={12} className="text-gray-400" />
                <span className="text-xs text-gray-500">{prestation.artisan.city}</span>
                {prestation.artisan.is_verified && (
                  <CheckCircle size={12} className="text-emerald-500 ml-1" />
                )}
              </div>
            </div>
          </div>
          
          {/* Rating */}
          <div className="flex items-center gap-1">
            <Star size={14} className="text-yellow-400 fill-yellow-400" />
            <span className="text-sm font-medium text-gray-700">{prestation.artisan.rating}</span>
            <span className="text-xs text-gray-400">({prestation.artisan.reviews_count})</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function PrestationsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { requireAuth } = useDemoMode();
  
  // États
  const [prestations, setPrestations] = useState([]);
  const [totalPrestations, setTotalPrestations] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  
  // Filtres
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [selectedMetier, setSelectedMetier] = useState(null);
  
  // Charger les prestations
  const fetchPrestations = async (search = null, metier = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (metier) params.append('metier', metier);
      params.append('per_page', '50');
      
      const response = await fetch(`${API_URL}/api/artisans/prestations?${params}`);
      const data = await response.json();
      
      if (data.prestations && data.prestations.length > 0) {
        setPrestations(data.prestations);
        setTotalPrestations(data.total);
      } else {
        // Fallback aux données démo
        let filtered = [...MOCK_PRESTATIONS];
        
        if (search) {
          const searchLower = search.toLowerCase();
          filtered = filtered.filter(p => 
            p.name.toLowerCase().includes(searchLower) ||
            p.description.toLowerCase().includes(searchLower)
          );
        }
        
        if (metier) {
          filtered = filtered.filter(p => p.metier === metier);
        }
        
        setPrestations(filtered);
        setTotalPrestations(filtered.length);
      }
    } catch (error) {
      console.error('Error fetching prestations:', error);
      // Fallback aux données démo
      let filtered = [...MOCK_PRESTATIONS];
      if (search) {
        const searchLower = search.toLowerCase();
        filtered = filtered.filter(p => 
          p.name.toLowerCase().includes(searchLower) ||
          p.description.toLowerCase().includes(searchLower)
        );
      }
      if (metier) {
        filtered = filtered.filter(p => p.metier === metier);
      }
      setPrestations(filtered);
      setTotalPrestations(filtered.length);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchPrestations(searchQuery || null, selectedMetier);
  }, [selectedMetier]);
  
  // Recherche
  const handleSearch = (e) => {
    e.preventDefault();
    fetchPrestations(searchQuery, selectedMetier);
  };
  
  // Clic sur prestation
  const handlePrestationClick = (prestation) => {
    navigate(`/prestations/${prestation.id}`);
  };
  
  // Filtre par métier
  const handleMetierFilter = (metierId) => {
    if (selectedMetier === metierId) {
      setSelectedMetier(null);
    } else {
      setSelectedMetier(metierId);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header & Navigation */}
      <UniversalBanner />
      <UnifiedHeader />
      <MainNavigation onOpenFilters={() => setShowFilters(true)} />
      
      <div className="flex">
        {/* Sidebar Ads - Gauche */}
        <AdSidebarLeft />
        
        {/* Contenu principal */}
        <main className="flex-1 max-w-7xl mx-auto px-4 py-6">
          {/* En-tête */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-[#1E3A5F]">Trouver une prestation</h1>
            <p className="text-gray-500 mt-1">Recherchez le service dont vous avez besoin</p>
          </div>
          
          {/* Barre de recherche + Bouton filtre */}
          <div className="flex gap-3 mb-6">
            <form onSubmit={handleSearch} className="flex-1 relative">
              <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher une prestation (ex: réparation fuite, peinture...)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-12 pl-12 pr-4 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </form>
            
            <FilterButton 
              onClick={() => setShowFilters(true)} 
              activeCount={Object.values(filters).filter(Boolean).length}
              context="prestations"
            />
          </div>
          
          {/* Filtres métiers horizontaux */}
          <div className="mb-6">
            <div className="flex gap-2 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
              <button
                onClick={() => setSelectedMetier(null)}
                className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${
                  !selectedMetier
                    ? 'bg-orange-500 text-white shadow-sm'
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                Tous
              </button>
              {METIERS.map(metier => {
                const Icon = metier.icon;
                return (
                  <button
                    key={metier.id}
                    onClick={() => handleMetierFilter(metier.id)}
                    className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${
                      selectedMetier === metier.id
                        ? 'bg-orange-500 text-white shadow-sm'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <Icon size={16} />
                    {metier.label}
                  </button>
                );
              })}
              {/* Flèche de scroll */}
              <div className="flex-shrink-0 flex items-center pointer-events-none pl-2">
                <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
              </div>
            </div>
          </div>
          
          {/* Compteur */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">
              {totalPrestations} prestation{totalPrestations > 1 ? 's' : ''} trouvée{totalPrestations > 1 ? 's' : ''}
            </p>
          </div>
          
          {/* Grille de prestations */}
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
            </div>
          ) : prestations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Wrench size={40} className="text-gray-300" />
              </div>
              <h3 className="font-bold text-gray-900 text-lg">Aucune prestation trouvée</h3>
              <p className="text-gray-500 mt-1">Essayez de modifier vos critères de recherche</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {prestations.map((prestation) => (
                <PrestationCard 
                  key={prestation.id} 
                  prestation={prestation}
                  onClick={handlePrestationClick}
                />
              ))}
            </div>
          )}
        </main>
        
        {/* Sidebar Ads - Droite */}
        <AdSidebarRight />
      </div>
      
      {/* Modal Filtres */}
      <UnifiedFilterSystem
        context="prestations"
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        onFiltersChange={setFilters}
        resultsCount={totalPrestations}
        onApply={() => {
          setShowFilters(false);
          // Appliquer les filtres
          if (filters.selected_metier) {
            setSelectedMetier(filters.selected_metier);
          }
          if (filters.type_prestation) {
            // Rechercher par type de prestation
            fetchPrestations(null, filters.selected_metier);
          }
        }}
      />
    </div>
  );
}
